import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Popover } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, IconButton, Button, List, ListItem, Tooltip, Chip, Paper } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import api from "../../services/api";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    maxHeight: 400,
    maxWidth: 450,
    padding: 0,
    overflowY: "scroll",
    overflowX: "hidden",
    borderRadius: 12,
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    ...(theme.scrollbarStyles || {}),
  },
  headerBar: {
    padding: "8px 12px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
  },
  listItem: {
    padding: theme.spacing(2.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: "pointer",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      paddingLeft: theme.spacing(3),
      "&::before": { opacity: 1 },
    },
    "&:last-child": { borderBottom: "none" },
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 3,
      backgroundColor: theme.palette.primary.main,
      opacity: 0,
      transition: "opacity 0.2s ease",
    },
  },
  listItemContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.2),
    width: "100%",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
  },
  title: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    lineHeight: 1.4,
    flex: 1,
    letterSpacing: "-0.01em",
  },
  chipContainer: {
    display: "flex",
    gap: theme.spacing(0.75),
    flexShrink: 0,
    alignItems: "center",
  },
  categoryChip: {
    height: 22,
    fontSize: "0.7rem",
    fontWeight: 500,
    borderRadius: 11,
    letterSpacing: "0.01em",
  },
  preview: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginTop: theme.spacing(0.5),
  },
  dateText: {
    fontSize: "0.75rem",
    color: theme.palette.text.disabled,
    fontWeight: 500,
    letterSpacing: "0.02em",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    "&::before": {
      content: '"•"',
      fontSize: "0.6rem",
      opacity: 0.6,
    },
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
    "&::before": {
      content: '"🧭"',
      fontSize: "2rem",
      opacity: 0.5,
    },
  },
}));

// Heurísticas para identificar eventos oriundos de grupos (não considerar no SLA)
const isLikelyGroup = (obj) => {
  try {
    if (!obj || typeof obj !== 'object') return false;
    // Flags explícitas
    if (obj.isGroup === true || obj.group === true || obj.contactIsGroup === true) return true;
    if (obj?.contact?.isGroup === true) return true;
    if (obj?.ticket?.isGroup === true || obj?.ticket?.contact?.isGroup === true) return true;
    // JIDs de grupo (WhatsApp): terminam com '@g.us'
    const jidCandidates = [
      obj.contactJid,
      obj.remoteJid,
      obj.chatId,
      obj.jid,
      obj?.key?.remoteJid,
      obj?.contact?.jid,
      obj?.ticket?.contact?.jid,
    ].filter(Boolean);
    if (jidCandidates.some((s) => typeof s === 'string' && /@g\.us$/i.test(s))) return true;
    // Alguns backends enviam 'contactName' com sufixo de grupo (não confiável, mas manter leve)
    return false;
  } catch {
    return false;
  }
};

const NotificationKanban = ({ anchorEl, open, onClose, onCountChange, volume = 1, registerUnlock }) => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const { get: getCompanySetting } = useCompanySettings();
  const [manualTicketCreationEnabled, setManualTicketCreationEnabled] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [planAdvanced, setPlanAdvanced] = useState(
    user?.company?.plan?.useKanbanAdvanced || user?.plan?.useKanbanAdvanced || user?.useKanbanAdvanced || false
  );
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [slaNotificationsEnabled, setSlaNotificationsEnabled] = useState(true);
  const audioCtxRef = useRef(null);
  const lastBeepAtRef = useRef(0);
  const resumeTriesRef = useRef(0);
  const seenIdsRef = useRef(new Set());
  const ticketIgnoreCacheRef = useRef(new Map());
  const assigneesSnapshotRef = useRef(new Map()); // ticketId -> { hash: string, userIds: number[] }
  const assigneesFetchStateRef = useRef(new Map()); // ticketId -> { inFlight: boolean, last: number }
  const slaStateByTicketRef = useRef(new Map());

  // Helpers para persistir visualizações (não lidas) no localStorage
  const STORAGE_KEY = "kanban.sla.seen";
  const loadSeen = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      seenIdsRef.current = new Set((Array.isArray(arr) ? arr : []).map(String));
    } catch { seenIdsRef.current = new Set(); }
  };
  const saveSeen = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(seenIdsRef.current)));
    } catch {}
  };
  useEffect(() => { loadSeen(); }, []);

  const advancedEnabled = useMemo(
    () => Boolean(planAdvanced) && (settingsLoaded ? Boolean(manualTicketCreationEnabled) : true),
    [planAdvanced, manualTicketCreationEnabled, settingsLoaded]
  );

  // Decide aceitar evento de SLA e atualiza estado local por ticket
  const shouldAcceptSlaEvent = (ticketId, type) => {
    try {
      const id = Number(ticketId);
      if (!id || Number.isNaN(id)) return true;
      const last = slaStateByTicketRef.current.get(id);
      if (type === 'clear') {
        if (last === 'warning' || last === 'overdue') {
          slaStateByTicketRef.current.set(id, 'clear');
          return true;
        }
        return false;
      }
      if (type === 'warning' || type === 'overdue') {
        slaStateByTicketRef.current.set(id, type);
        return true;
      }
      return true;
    } catch {
      return true;
    }
  };

  // Verifica (com cache) se um ticket deve ser ignorado no SLA (grupo ou sessão não-oficial)
  const shouldIgnoreTicketForSlaById = async (ticketId) => {
    try {
      const id = Number(ticketId);
      if (!id || Number.isNaN(id)) return false;
      if (ticketIgnoreCacheRef.current.has(id)) return ticketIgnoreCacheRef.current.get(id) === true;
      const { data } = await api.get(`/tickets/${id}`);
      const t = data?.ticket || data || {};
      const isG = Boolean(t?.contact?.isGroup || t?.isGroup || t?.contactIsGroup);
      const isSession = (t?.isOfficial === false);
      const ignore = isG || isSession;
      ticketIgnoreCacheRef.current.set(id, ignore);
      return ignore;
    } catch {
      return false;
    }
  };

  // Carrega flags de criação manual e preferência de SLA
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const setting = await getCompanySetting({ column: "manualTicketCreation" });
        if (!mounted) return;
        setManualTicketCreationEnabled(setting?.manualTicketCreation === "enabled");
      } catch {
        setManualTicketCreationEnabled(false);
      } finally {
        if (mounted) setSettingsLoaded(true);
      }
      // Preferência de notificações de SLA
      try {
        const pref = await getCompanySetting({ column: "slaNotifications" });
        if (mounted) setSlaNotificationsEnabled(String(pref?.data || pref?.value || "enabled") === "enabled");
      } catch {
        if (mounted) setSlaNotificationsEnabled(true);
      }
    })();
    return () => { mounted = false; };
  }, [getCompanySetting, user?.companyId]);

  // Preload de eventos recentes (quando avançado está ligado)
  useEffect(() => {
    (async () => {
      if (!slaNotificationsEnabled || (settingsLoaded && !advancedEnabled)) {
        setEvents([]);
        onCountChange && onCountChange(0);
        return;
      }
      try {
        setLoading(true);
        const { data } = await api.get("/sla-events/recent", { params: { limit: 25, hours: 24 } });
        // Filtra grupos já no preload
        const arr = (Array.isArray(data) ? data : (data?.items || []))
          .filter((e) => !isLikelyGroup(e) && !(e?.isOfficial === false));

        // Ordena cronologicamente ascendente para construir histórico
        arr.sort((a, b) => new Date(a.createdAt || a.updatedAt || 0) - new Date(b.createdAt || b.updatedAt || 0));

        // Reinicia o estado por ticket
        slaStateByTicketRef.current = new Map();

        const built = [];
        for (const e of arr) {
          const action = e.action || e.type;
          const type = action === 'sla_overdue' || action === 'overdue' ? 'overdue' : action === 'sla_warning' || action === 'warning' ? 'warning' : 'clear';
          const ticketId = e.ticketId || e.ticket?.id || null;

          if (ticketId) {
            const ignore = await shouldIgnoreTicketForSlaById(ticketId);
            if (ignore) continue;
          }

          // Aceita "clear" apenas se houver histórico de warning/overdue
          if (!shouldAcceptSlaEvent(ticketId, type)) continue;

          const label = e.ticketLabel || e.subject || e.contactName || (e.ticketId ? `#${e.ticketId}` : "");
          const title = type === 'warning' ? 'SLA vencendo' : type === 'overdue' ? 'SLA vencido' : 'SLA normalizado';
          const text = type === 'warning' ? `Ticket ${label} está próximo do prazo.` : type === 'overdue' ? `Ticket ${label} ultrapassou o prazo.` : `Ticket ${label} voltou ao normal.`;
          const eventId = e.id;
          built.push({
            id: `sla-${eventId}`,
            eventId,
            ticketId,
            type,
            title,
            text,
            createdAt: e.createdAt || e.updatedAt || new Date().toISOString(),
            viewed: eventId ? seenIdsRef.current.has(String(eventId)) : false,
          });
        }

        // Mostra mais recentes primeiro
        const finalList = built.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setEvents(finalList);
        const unread = finalList.filter(ev => !ev.viewed).length;
        onCountChange && onCountChange(unread);
      } catch {
        setEvents([]);
        onCountChange && onCountChange(0);
      } finally {
        setLoading(false);
      }
    })();
  }, [advancedEnabled, onCountChange, slaNotificationsEnabled, settingsLoaded]);

  // Web Audio helpers (sons de notificação)
  const ensureAudioContext = async () => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        audioCtxRef.current = new Ctx();
      }
      if (audioCtxRef.current?.state === "suspended") {
        await audioCtxRef.current.resume();
      }
      return audioCtxRef.current;
    } catch (_) {
      return null;
    }
  };

  // Disponibiliza função de desbloqueio para o pai (AppBar)
  useEffect(() => {
    if (typeof registerUnlock === 'function') {
      registerUnlock(async () => {
        try { await ensureAudioContext(); } catch(_) {}
      });
    }
  }, [registerUnlock]);

  const playNotificationBeep = async (type = "info") => {
    try {
      // Tons: overdue (chamativo), clear/normalizado (discreto), assign (discreto)
      if (type !== "overdue" && type !== "clear" && type !== "assign") return;

      const now = Date.now();
      if (now - lastBeepAtRef.current < 1500) return;
      lastBeepAtRef.current = now;

      const ctx = await ensureAudioContext();
      if (!ctx) return;

      const createSineBeep = (frequency, startDelayMs, durationMs, attackMs, decayMs, releaseMs, gainLevel) => {
        const startAt = ctx.currentTime + (startDelayMs || 0) / 1000;
        const dur = (durationMs || 140) / 1000;
        const atk = (attackMs || 8) / 1000;
        const dec = (decayMs || 100) / 1000;
        const rel = (releaseMs || 30) / 1000;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const level = Math.max(0, Math.min(1, gainLevel || 0.6)) * Math.max(0, Math.min(1, volume || 1));
        osc.type = "sine";
        osc.frequency.setValueAtTime(frequency, startAt);
        gain.gain.setValueAtTime(0.0001, startAt);
        gain.gain.linearRampToValueAtTime(level, startAt + atk);
        gain.gain.linearRampToValueAtTime(level * 0.7, startAt + atk + dec);
        gain.gain.exponentialRampToValueAtTime(0.0001, startAt + atk + dec + rel + dur);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(startAt);
        osc.stop(startAt + atk + dec + rel + dur + 0.02);
      };

      if (type === "overdue") {
        // Dois bipes curtos ascendentes (980Hz -> 1040Hz)
        createSineBeep(980, 0, 170, 8, 140, 40, 0.9);
        createSineBeep(1040, 120, 170, 8, 140, 40, 0.9);
      } else if (type === "clear") {
        // Um bipe único e discreto (650Hz)
        createSineBeep(650, 0, 120, 8, 80, 25, 0.6);
      } else if (type === "assign") {
        // Um bipe único discreto (720Hz)
        createSineBeep(720, 0, 130, 8, 90, 25, 0.65);
      }
    } catch (_) {
      // Fallback simples usando elemento de áudio (curto)
      try {
        if (type === "overdue" || type === "clear" || type === "assign") {
          const audio = new Audio("data:audio/wav;base64,UklGRhQFAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQwFAAABAQEBAQEBAQEBAQEB");
          audio.volume = Math.max(0, Math.min(1, volume || 1));
          audio.play().catch(() => {});
        }
      } catch {}
    }
  };

  // Sockets: plano, settings, canal de SLA e canal de responsáveis
  useEffect(() => {
    const companyId = user?.companyId;
    if (!companyId || !socket) return;

    const onCompanySettings = (data) => {
      if (data?.action !== "update") return;
      const key = data?.setting?.key;
      const value = data?.setting?.value;
      if (key === "manualTicketCreation") {
        setManualTicketCreationEnabled(value === "enabled");
        setSettingsLoaded(true);
      }
      if (key === "slaNotifications") {
        setSlaNotificationsEnabled(String(value || "enabled") === "enabled");
      }
    };
    const onAssignees = async (payload) => {
      try {
        // Espera-se: { action: 'add'|'remove'|'replace', ticketId, userId, byUserId? }
        const action = payload?.action;
        const ticketId = payload?.ticketId || payload?.ticket?.id;
        if (!ticketId) return;
        // Ignorar grupos/sessões (mesma regra do SLA por segurança)
        if (isLikelyGroup(payload) || payload?.isOfficial === false) return;

        const meId = user?.id;
        if (!meId) return;

        if (action === 'add') {
          const targetUserId = payload?.userId;
          if (!targetUserId) return;
          if (String(targetUserId) !== String(meId)) return;

          const label = payload?.ticketLabel || (ticketId ? `#${ticketId}` : "");
          const title = "Ticket atribuído a você";
          const text = label ? `O ticket ${label} foi atribuído a você.` : "Um novo ticket foi atribuído a você.";
          const item = {
            id: `assign-${ticketId}-${Date.now()}`,
            eventId: null,
            ticketId,
            type: 'assign',
            title,
            text,
            createdAt: new Date().toISOString(),
            viewed: Boolean(open),
          };
          setEvents((prev) => {
            const next = Array.isArray(prev) ? [...prev] : [];
            next.unshift(item);
            const unread = next.filter(ev => !ev.viewed).length;
            onCountChange && onCountChange(unread);
            playNotificationBeep('assign');
            return next;
          });
          // Atualiza snapshot localmente sem requisitar API
          try {
            const key = Number(ticketId);
            const prev = assigneesSnapshotRef.current.get(key) || { userIds: [] };
            const ids = Array.from(new Set([...(prev.userIds || []), Number(meId)].filter(n => !Number.isNaN(n))));
            const hash = JSON.stringify([...ids].sort((a,b)=>a-b));
            assigneesSnapshotRef.current.set(key, { hash, userIds: ids });
          } catch {}
          return;
        }

        if (action === 'replace') {
          // Buscar lista atual de responsáveis para detectar se agora inclui o usuário logado
          try {
            const key = Number(ticketId);
            const now = Date.now();
            const state = assigneesFetchStateRef.current.get(key) || { inFlight: false, last: 0 };
            if (state.inFlight || (now - (state.last || 0) < 4000)) return; // throttle 4s por ticket
            assigneesFetchStateRef.current.set(key, { inFlight: true, last: state.last || 0 });

            const { data } = await api.get(`/tickets/${ticketId}/assignees`);
            const users = Array.isArray(data) ? data : (data?.items || []);
            const ids = users.map(u => Number(u.userId)).filter(n => !Number.isNaN(n));
            const includesMe = ids.some(id => String(id) === String(meId));
            const hash = JSON.stringify(ids.sort((a,b)=>a-b));
            const prev = assigneesSnapshotRef.current.get(key);
            const prevIncludedMe = prev ? (prev.userIds || []).some(id => String(id) === String(meId)) : false;
            // Atualiza snapshot
            assigneesSnapshotRef.current.set(key, { hash, userIds: ids });
            assigneesFetchStateRef.current.set(key, { inFlight: false, last: now });
            // Notifica apenas se passou a incluir o usuário
            if (!includesMe || prevIncludedMe) return;

            const label = payload?.ticketLabel || (ticketId ? `#${ticketId}` : "");
            const title = "Ticket atribuído a você";
            const text = label ? `O ticket ${label} foi atribuído a você.` : "Um novo ticket foi atribuído a você.";
            const item = {
              id: `assign-${ticketId}-${Date.now()}`,
              eventId: null,
              ticketId,
              type: 'assign',
              title,
              text,
              createdAt: new Date().toISOString(),
              viewed: Boolean(open),
            };
            setEvents((prevEvts) => {
              const next = Array.isArray(prevEvts) ? [...prevEvts] : [];
              next.unshift(item);
              const unread = next.filter(ev => !ev.viewed).length;
              onCountChange && onCountChange(unread);
              playNotificationBeep('assign');
              return next;
            });
          } catch {}
          return;
        }
      } catch {}
    };
    const onPlan = (data) => {
      if (data?.action !== "update") return;
      const useAdv = Boolean(data?.plan?.useKanbanAdvanced);
      setPlanAdvanced(useAdv);
    };
    const onSla = async (payload) => {
      if (!slaNotificationsEnabled) return;
      if (settingsLoaded && !advancedEnabled) return;
      // Ignorar eventos de grupos
      if (isLikelyGroup(payload) || payload?.isOfficial === false) return;
      const action = payload?.action || payload?.type;
      const type = action === 'sla_overdue' || action === 'overdue' ? 'overdue' : action === 'sla_warning' || action === 'warning' ? 'warning' : 'clear';
      const label = payload?.ticketLabel || payload?.subject || payload?.contactName || (payload?.ticketId ? `#${payload.ticketId}` : "");
      const title = type === 'warning' ? 'SLA vencendo' : type === 'overdue' ? 'SLA vencido' : 'SLA normalizado';
      const text = type === 'warning' ? `Ticket ${label} está próximo do prazo.` : type === 'overdue' ? `Ticket ${label} ultrapassou o prazo.` : `Ticket ${label} voltou ao normal.`;
      const eventId = payload?.id || payload?.eventId;
      const ticketId = payload?.ticketId || null;
      if (ticketId) {
        const ignore = await shouldIgnoreTicketForSlaById(ticketId);
        if (ignore) return;
      }
      // Aceita "clear" apenas se houver histórico de warning/overdue
      if (!shouldAcceptSlaEvent(ticketId, type)) return;
      const item = {
        id: eventId ? `sla-${eventId}` : `sla-${payload?.ticketId || 'tmp'}-${Date.now()}`,
        eventId: eventId || null,
        ticketId,
        type,
        title,
        text,
        createdAt: new Date().toISOString(),
        viewed: eventId ? seenIdsRef.current.has(String(eventId)) || Boolean(open) : Boolean(open),
      };
      setEvents((prev) => {
        const next = Array.isArray(prev) ? [...prev] : [];
        if (!next.find((e) => String(e.id) === String(item.id))) {
          next.unshift(item);
        }
        const unread = next.filter(ev => !ev.viewed).length;
        onCountChange && onCountChange(unread);
        // Som apenas para overdue/clear (já filtrado em playNotificationBeep)
        playNotificationBeep(type);
        return next;
      });
    };

    const chSettings = `company-${companyId}-companySettings`;
    const chPlan = `company-${companyId}-plan`;
    const chSla = `company-${companyId}-sla`;
    const chAssignees = `company-${companyId}-ticket-assignees`;
    socket.on(chSettings, onCompanySettings);
    socket.on(chPlan, onPlan);
    socket.on(chSla, onSla);
    socket.on(chAssignees, onAssignees);
    return () => {
      socket.off(chSettings, onCompanySettings);
      socket.off(chPlan, onPlan);
      socket.off(chSla, onSla);
      socket.off(chAssignees, onAssignees);
    };
  }, [socket, user?.companyId, advancedEnabled, onCountChange, slaNotificationsEnabled, settingsLoaded]);

  const handleDismiss = async (id, isLocal = false) => {
    try {
      if (!isLocal && id) {
        await api.post(`/sla-events/${id}/dismiss`);
      }
      setEvents((prev) => {
        const next = (prev || []).filter((e) => String(e.eventId) !== String(id) && String(e.id) !== String(id));
        const unread = next.filter(ev => !ev.viewed).length;
        onCountChange && onCountChange(unread);
        return next;
      });
    } catch {}
  };

  const handleClearAll = async () => {
    try {
      const ids = (events || []).map((e) => e.eventId).filter(Boolean);
      if (ids.length > 0) {
        await api.post(`/sla-events/dismiss/bulk`, { ids });
      }
      setEvents([]);
      onCountChange && onCountChange(0);
    } catch {}
  };

  // Quando o popover abre, marca todas como visualizadas (não contam no badge)
  useEffect(() => {
    if (open && advancedEnabled) {
      setEvents((prev) => {
        const next = (prev || []).map(ev => {
          if (!ev.viewed) {
            if (ev.eventId) seenIdsRef.current.add(String(ev.eventId));
            return { ...ev, viewed: true };
          }
          return ev;
        });
        saveSeen();
        onCountChange && onCountChange(0);
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, advancedEnabled]);

  const handleOpenItem = (ev) => {
    try {
      const ticketId = ev?.ticketId;
      if (!ticketId) return;
      const path = (window?.location?.pathname || "").toLowerCase();
      if (path.includes("kanban")) {
        try { window.dispatchEvent(new CustomEvent("open-ticket-modal", { detail: { ticketId } })); } catch {}
      } else {
        history.push("/kanban", { openTicketId: ticketId });
      }
      if (typeof onClose === 'function') onClose();
    } catch {}
  };

  const emptyText = i18n.t("notifications.noTickets", { defaultValue: "Nenhuma notificação." });

  return (
    <Popover
      open={Boolean(open)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      PaperProps={{ style: { maxWidth: "90vw", overflow: "hidden" } }}
      TransitionProps={{ onEnter: () => { try { ensureAudioContext(); } catch(_) {} } }}
    >
      <Paper variant="outlined" className={classes.mainPaper}>
        <div className={classes.headerBar}>
          {(() => { const hasActions = (events || []).length > 0; return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: hasActions ? 'space-between' : 'center', gap: 8, margin: "4px 4px 8px" }}>
            <div style={{ fontSize: "0.78rem", fontWeight: 600, color: 'inherit' }}>Alertas de SLA</div>
            {hasActions && (
              <div>
                <Button size="small" onClick={handleClearAll} style={{ marginRight: 6 }}>
                  {i18n.t("notifications.clearAll", { defaultValue: "Limpar todas" })}
                </Button>
              </div>
            )}
          </div>
          ); })()}
        </div>
        {loading ? (
          <Typography variant="body2" style={{ padding: 16 }}>Carregando…</Typography>
        ) : (
          <List component="div" style={{ padding: 0 }}>
            {(!advancedEnabled && settingsLoaded && (events || []).length === 0) ? (
              <div className={classes.emptyState}>
                <Typography>Tickets Avançados desativados.</Typography>
              </div>
            ) : (events || []).length === 0 ? (
              <div className={classes.emptyState}>
                <Typography>{emptyText}</Typography>
              </div>
            ) : (
              (events || []).map((ev) => {
                const type = ev.action || ev.type;
                const chip = {
                  label: type === 'sla_overdue' || type === 'overdue' ? 'Vencido'
                        : type === 'sla_warning' || type === 'warning' ? 'Vencendo'
                        : type === 'assign' ? 'Atribuído'
                        : 'Normalizado',
                  bg: type === 'sla_overdue' || type === 'overdue' ? '#e53e3e20'
                      : type === 'sla_warning' || type === 'warning' ? '#f59e0b20'
                      : type === 'assign' ? '#2563eb20'
                      : '#38a16920',
                  color: type === 'sla_overdue' || type === 'overdue' ? '#e53e3e'
                         : type === 'sla_warning' || type === 'warning' ? '#f59e0b'
                         : type === 'assign' ? '#2563eb'
                         : '#38a169',
                  border: type === 'sla_overdue' || type === 'overdue' ? '#e53e3e40'
                         : type === 'sla_warning' || type === 'warning' ? '#f59e0b40'
                         : type === 'assign' ? '#2563eb40'
                         : '#38a16940',
                };
                const title = ev.title || ev.message || (ev.ticketId ? `Ticket #${ev.ticketId}` : "Alerta de SLA");
                const ts = ev.createdAt || ev.updatedAt;
                return (
                  <ListItem key={ev.id || Math.random()} className={classes.listItem} disableGutters onClick={() => handleOpenItem(ev)}>
                    <div className={classes.listItemContent}>
                      <div className={classes.headerRow}>
                        <Typography className={classes.title}>{title}</Typography>
                        <div className={classes.chipContainer}>
                          <Chip
                            label={chip.label}
                            size="small"
                            className={classes.categoryChip}
                            style={{ backgroundColor: chip.bg, color: chip.color, border: `1px solid ${chip.border}` }}
                          />
                          {(ev.eventId || ev.id) && (
                            <Tooltip title={i18n.t("notifications.dismiss", { defaultValue: "Ocultar" })}>
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDismiss(ev.eventId || ev.id, !ev.eventId); }}>
                                <CloseIcon style={{ width: 16, height: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      {(ev.text || ev.preview) && (
                        <Typography className={classes.preview}>{ev.text || ev.preview}</Typography>
                      )}
                      { (ts || ev.createdAt) && (
                        <Typography className={classes.dateText}>{moment(ts || ev.createdAt).format("DD/MM/YYYY HH:mm")}</Typography>
                      )}
                    </div>
                  </ListItem>
                );
              })
            )}
          </List>
        )}
      </Paper>
    </Popover>
  );
};

export default NotificationKanban;
